import ScrollModal from "@/components/common/scroll-modal";
import {
  Heading,
  Button,
  Icon,
  GroupTextFieldRow,
  GroupTextField,
  Input,
} from "@elaniintc/goes-design-system";
import { DialogTitle, DialogDescription } from "@/components/ui/dialog";
import type { AxiosError } from "axios";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { newColumnSchema, type ColumnData } from "@/schemas/data-center";
import SelectField from "@/components/common/select";
import { useAddNewColumnInTable } from "@/queries/data-center/data-center.api";
import { OptionTooltip } from "@/workflow/components/configurations/form/components/OptionTooltip";
import { useEffect, useState } from "react";

interface NewColumnModal {
  open: boolean;
  tableId: string;
  name: string;
  description?: string;
  columns?: {
    id: string;
    name: string;
    type: string;
    display_name: string;
  }[];
  onClose: () => void;
  onSuccess: () => void;
}

const initialState: ColumnData = {
  display_name: "",
  name: "",
  data_type: "STRING",
};

// eslint-disable-next-line react-refresh/only-export-components
export const columnDataTypeOptions = [
  {
    value: "STRING",
    label: "Texto Plano",
    icon: <Icon type="far" name="text" />,
    iconPosition: "left",
  },
  {
    value: "INTEGER",
    label: "Números",
    icon: <Icon type="far" name="hashtag" />,
    iconPosition: "left",
  },
  {
    value: "FLOAT",
    label: "Decimales",
    icon: <Icon type="far" name="hashtag" />,
    iconPosition: "left",
  },
  {
    value: "BOOLEAN",
    label: "Verdadero/Falso",
    icon: <Icon type="far" name="toggle-on" />,
    iconPosition: "left",
  },
];

export default function NewColumnModal({
  open,
  tableId,
  name,
  description,
  columns,
  onClose,
  onSuccess,
}: Readonly<NewColumnModal>) {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isValid },
    clearErrors,
    reset,
    watch,
    setValue,
  } = useForm({
    resolver: zodResolver(newColumnSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: initialState,
  });

  const { mutateAsync: addColumns, isPending: isLoadingSave } =
    useAddNewColumnInTable();

  const [locked, setLocked] = useState(true);
  const [data_type, columnName] = watch(["data_type", "display_name"]);

  const onAddColumn = async (column: {
    name: string;
    type: string;
    display_name: string;
  }) => {
    try {
      clearErrors();
      await addColumns({
        id: tableId,
        name: name || "",
        ...(description
          ? {
              description: description,
            }
          : {}),
        columns: [
          ...(columns || []).map(
            (col: {
              id: string;
              name: string;
              type: string;
              display_name: string;
            }) => ({
              id: col.id,
              name: col.name,
              type: col.type as "STRING" | "INTEGER" | "BOOLEAN" | "FLOAT",
              display_name: col.display_name,
            }),
          ),
          {
            name: column.name.toLowerCase(),
            type: column.type as "STRING" | "INTEGER" | "BOOLEAN" | "FLOAT",
            display_name: column.display_name || "",
          },
        ],
      });

      onSuccess();
    } catch (e) {
      const error = e as AxiosError<{
        message?: string;
        errors?: { [key: string]: string[] };
      }>;
      setError("name", {
        type: "server",
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Error al crear la columna",
      });
    }
  };

  const onSubmit = async (data: ColumnData) => {
    onAddColumn({
      name: data.name,
      type: data.data_type,
      display_name: data.display_name,
    });
  };

  const handleClose = () => {
    reset({
      name: "",
      display_name: "",
      data_type: "STRING",
    });
    onClose();
  };

  const handleLockToggle = () => {
    setLocked((l) => !l);
  };

  useEffect(() => {
    if (columnName && locked) {
      const generatedKey = columnName
        .toLocaleLowerCase()
        .trim()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, "_");

      setValue("name", generatedKey, { shouldValidate: true });
    }
  }, [columnName, setValue, locked]);

  return (
    <ScrollModal
      isOpen={open}
      onClose={handleClose}
      modalContentClassName="w-full max-w-[520px] max-h-[680px] flex flex-col"
      header={
        <div className="flex items-center justify-between px-8 py-3">
          <DialogTitle className="sr-only">Nueva Columna</DialogTitle>
          <DialogDescription className="sr-only">
            Formulario para crear una nueva columna para la tabla
          </DialogDescription>
          <Heading variant="h5" className="mb-0!">
            Nueva Columna
          </Heading>
          <Button
            type="button"
            onClick={handleClose}
            variant="quiet"
            aria-label="Cerrar"
            className="w-8 h-8 cursor-pointer"
          >
            <Icon type="far" name="times" />
          </Button>
        </div>
      }
      footer={
        <div className="w-full grid grid-cols-1 tablet:grid-cols-2 py-3 px-8 gap-3">
          <Button
            variant="tertiary"
            size="sm"
            type="button"
            onClick={handleClose}
            className="min-w-30 cursor-pointer"
          >
            Cancelar
          </Button>
          <Button
            variant="default"
            size="sm"
            type="submit"
            disabled={!isValid || isLoadingSave}
            form="new-column-form"
            className="min-w-30 cursor-pointer"
          >
            Crear Columna
            {isLoadingSave && (
              <Icon
                type="far"
                name="spinner-third"
                className="animate-spin ml-2"
              />
            )}
          </Button>
        </div>
      }
    >
      <form
        id="new-column-form"
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col flex-1 min-h-0 px-4 sm:px-8"
      >
        {/* Contenido scrollable */}
        <div className="flex flex-col gap-6 pb-6">
          {/* Detalles */}
          <div>
            <Heading variant="h6" className="mb-4">
              Detalles
            </Heading>
            <div className="flex flex-col gap-4">
              <GroupTextField
                label="Nombre de la columna"
                required
                error={
                  errors.display_name?.message || errors.name?.message || ""
                }
              >
                <GroupTextFieldRow>
                  <Input
                    className="w-full"
                    required
                    maxLength={100}
                    placeholder="Escribe un nombre para la columna"
                    value={columnName.toUpperCase()}
                    {...register("display_name")}
                  />
                </GroupTextFieldRow>
                <GroupTextFieldRow>
                  <div className="flex items-center w-full relative">
                    <Input
                      className="w-full pr-12"
                      placeholder="key"
                      disabled={locked}
                      group="down-left"
                      {...register("name")}
                    />
                    <OptionTooltip title="La key es el identificador técnico del campo. Se recomienda no modificarla si ya se ha utilizado en integraciones o automatizaciones.">
                      <div className="absolute right-4 top-3 text-body">
                        <Icon name="circle-info" type="far" />
                      </div>
                    </OptionTooltip>
                  </div>

                  <Button
                    variant="quiet"
                    type="button"
                    onClick={handleLockToggle}
                    aria-label={
                      locked
                        ? "Desbloquear edición de key"
                        : "Bloquear edición de key"
                    }
                  >
                    <Icon name={locked ? "lock" : "lock-open"} type="far" />
                  </Button>
                </GroupTextFieldRow>
              </GroupTextField>

              <SelectField
                label="Tipo de datos"
                placeholder="Selecciona una opción"
                name="data_type"
                required
                value={data_type || ""}
                options={columnDataTypeOptions}
                onChange={(value) => {
                  setValue("data_type", value as ColumnData["data_type"]);
                }}
                isLoading={false}
              />
            </div>
          </div>
        </div>
      </form>
    </ScrollModal>
  );
}
